import React, { ReactNode, useState, useContext } from "react";
import { Button, makeStyles } from "@material-ui/core";
import { colors, greys } from "@styles/color.styles";
import { fontFamilyHelvetica, weight } from "@styles/type.styles";
import classNames from "classnames";
import { NextDefragRunTag } from "@ui/next-defrag-run-tag";
import {
  DefragRunFlowState,
  getDefragRunTagLabelFromMillis,
  EventAutopilotStatusCardStatus,
  getLocalDefragRunTime,
} from "@util/event-history.util";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";

export interface EventAutopilotStatusCardProps {
  status: EventAutopilotStatusCardStatus;
  planRefresh: string;
  isUserInPaidPlan: boolean;
  eventOrganizerEmail: string;
  canUserInviteOrganizerToPlan: boolean;
  nextDefragTimeMillis: number;
  onUnpauseEvent: () => void;
  onViewPlans: () => void;
  onInviteOrganizerToBillingGroup: (hasPermission: boolean) => void;
}

const useStyles = makeStyles({
  container: {
    border: `1px solid ${greys.borderGrey}`,
    borderRadius: 4,
    background: greys.white,
    padding: "6px 10px",
    marginBottom: 14,
  },
  container__interactive: {
    padding: "6px 10px 2px",
  },
  title: {
    fontSize: `${13 / 16}rem`,
    fontWeight: weight.bold,
    lineHeight: 1.25,
    fontFamily: fontFamilyHelvetica,
    margin: "4px 0px",
    color: colors.green.darker,
  },
  title__warning: {
    color: colors.peach.darker,
  },
  description: {
    fontFamily: fontFamilyHelvetica,
    margin: "4px 0px",
    color: greys.slate,
    fontSize: `${12 / 16}rem`,
    fontWeight: weight.std,
    lineHeight: 1.33,
  },
  button: {
    marginLeft: "-4px",
    color: greys.slate,
  },
  button__warning: {
    marginLeft: "-4px",
    color: colors.peach.darker,
  },
  button__success: {
    marginLeft: "-4px",
    color: "white",
    width: "100%",
    marginTop: "10px",
    backgroundColor: colors.green.darker,
    "&:hover": {
      backgroundColor: colors.green.standard,
    },
    "&:disabled": {
      backgroundColor: greys[1],
      color: greys[2],
      "&:hover": {
        backgroundColor: greys[1],
      },
    },
  },
});

const OverQuotaWithInvite = ({
  planRefresh,
  eventOrganizerEmail,
  canUserInviteOrganizerToPlan,
  onInviteOrganizerToBillingGroup,
}: {
  planRefresh: string;
  eventOrganizerEmail: string;
  canUserInviteOrganizerToPlan: boolean;
  onInviteOrganizerToBillingGroup: (hasPermission: boolean) => void;
}) => {
  const [requestSent, setRequestSent] = useState<boolean>(false);

  const classes = useStyles();
  const organizerEmail = eventOrganizerEmail ? ` (${eventOrganizerEmail})` : "";

  let description = `The meeting organizer${organizerEmail} is over their free plan limit. This event won't move until the account is upgraded or their free plan resets`;
  if (planRefresh.length > 0) {
    description += ` on ${planRefresh}`;
  }

  const onSelectInviteOrganizer = (hasPermission: boolean) => {
    onInviteOrganizerToBillingGroup(hasPermission);
    setRequestSent(true);
  };

  return (
    <>
      <p className={classNames(classes.title, classes.title__warning)}>
        Organizer's free quota exceeded
      </p>
      <div className={classes.description}>
        {description}
        {canUserInviteOrganizerToPlan ? (
          <Button
            size="small"
            variant="text"
            disabled={requestSent}
            className={classes.button__success}
            onClick={() => {
              onSelectInviteOrganizer(true);
            }}
          >
            {requestSent ? "Invite Sent" : "Add to my paid plan"}
          </Button>
        ) : (
          <Button
            size="small"
            variant="text"
            disabled={requestSent}
            className={classes.button__success}
            onClick={() => {
              onSelectInviteOrganizer(false);
            }}
          >
            {requestSent ? "Request Sent" : "Ask admin to add user"}
          </Button>
        )}
      </div>
    </>
  );
};

const OverQuota = ({
  planRefresh,
  onViewPlans,
  isUserInPaidPlan,
  eventOrganizerEmail,
  canUserInviteOrganizerToPlan,
  onInviteOrganizerToBillingGroup,
}: {
  planRefresh: string;
  onViewPlans: () => void;
  isUserInPaidPlan?: boolean;
  eventOrganizerEmail: string;
  canUserInviteOrganizerToPlan: boolean;
  onInviteOrganizerToBillingGroup: (hasPermission: boolean) => void;
}) => {
  if (isUserInPaidPlan) {
    return (
      <OverQuotaWithInvite
        eventOrganizerEmail={eventOrganizerEmail}
        canUserInviteOrganizerToPlan={canUserInviteOrganizerToPlan}
        planRefresh={planRefresh}
        onInviteOrganizerToBillingGroup={onInviteOrganizerToBillingGroup}
      />
    );
  }
  const classes = useStyles();
  let description =
    "Clockwise will stop scheduling holds and events until you upgrade or the quota refreshes";
  if (planRefresh.length > 0) {
    description += ` on ${planRefresh}`;
  }

  return (
    <>
      <p className={classNames(classes.title, classes.title__warning)}>Free quota exceeded</p>
      <div className={classes.description}>
        {description}
        <Button
          size="small"
          variant="text"
          className={classes.button__warning}
          onClick={onViewPlans}
        >
          View Plans
        </Button>
      </div>
    </>
  );
};

const EventOutsideTimeRange = () => {
  const classes = useStyles();

  return (
    <>
      <p className={classes.title}>This meeting's timing is set</p>
      <p className={classes.description}>
        Clockwise will not change meetings on your calendar that occur within the next 24 hours or
        in the past.
      </p>
    </>
  );
};

const EventExceedsDefragRange = ({ nextDefragRunMillis }: { nextDefragRunMillis: number }) => {
  const classes = useStyles();

  return (
    <>
      <p className={classes.title}>Clockwise may update this meeting's timing</p>
      <p className={classes.description}>
        Clockwise will search for a better meeting time every weekday at&nbsp;
        {getLocalDefragRunTime(nextDefragRunMillis)}.
      </p>
    </>
  );
};

const CanMove = ({ nextDefragRunMillis }: { nextDefragRunMillis: number }) => {
  const classes = useStyles();

  const { flowState } = getDefragRunTagLabelFromMillis(nextDefragRunMillis);
  const searchTense = flowState === DefragRunFlowState.Running ? "searches" : "will search";

  return (
    <>
      <p className={classes.title}>
        Clockwise will optimize this meeting's timing&nbsp;
        <NextDefragRunTag millis={nextDefragRunMillis} />
      </p>
      <p className={classes.description}>
        Clockwise {searchTense} for a better meeting time every weekday at&nbsp;
        {getLocalDefragRunTime(nextDefragRunMillis)}.
      </p>
    </>
  );
};

const Paused = ({ onUnpauseEvent }: { onUnpauseEvent: () => void }) => {
  const classes = useStyles();
  const t = useContext(TranslationContext);

  return (
    <>
      <p className={classNames(classes.title, classes.title__warning)}>
        {t("autopilot-status:this-meeting-paused")}
      </p>
      <div className={classes.description}>
        This meeting was manually rescheduled to a specific time. Clockwise will not move it.
        <Button className={classes.button} size="small" variant="text" onClick={onUnpauseEvent}>
          Let it move again
        </Button>
      </div>
    </>
  );
};

export const EventAutopilotStatusCard = ({
  status,
  planRefresh,
  nextDefragTimeMillis,
  isUserInPaidPlan,
  canUserInviteOrganizerToPlan,
  eventOrganizerEmail,
  onUnpauseEvent,
  onViewPlans,
  onInviteOrganizerToBillingGroup,
}: EventAutopilotStatusCardProps) => {
  const classes = useStyles();

  const STATUS_TO_DESCRIPTION_MAP: Record<EventAutopilotStatusCardStatus, ReactNode> = {
    OverQuota: (
      <OverQuota
        isUserInPaidPlan={isUserInPaidPlan}
        planRefresh={planRefresh}
        onViewPlans={onViewPlans}
        canUserInviteOrganizerToPlan={canUserInviteOrganizerToPlan}
        eventOrganizerEmail={eventOrganizerEmail}
        onInviteOrganizerToBillingGroup={onInviteOrganizerToBillingGroup}
      />
    ),
    EventOutsideTimeRange: <EventOutsideTimeRange />,
    EventExceedsDefragRange: <EventExceedsDefragRange nextDefragRunMillis={nextDefragTimeMillis} />,
    CanMove: <CanMove nextDefragRunMillis={nextDefragTimeMillis} />,
    Paused: <Paused onUnpauseEvent={onUnpauseEvent} />,
    Pinned: null,
    NoDuration: null,
    External: null,
    PrivateEvent: null,
    SmartHold: null,
  };

  if (!STATUS_TO_DESCRIPTION_MAP[status]) {
    return null;
  }

  return (
    <div
      className={classNames(classes.container, {
        [classes.container__interactive]: status === "OverQuota" || status === "Paused",
      })}
      cw-id="event-status-card"
    >
      {STATUS_TO_DESCRIPTION_MAP[status]}
    </div>
  );
};
